/**
 * Acquire failure → member copy + retryability.
 *
 * Key off the response body's `error` string (not only HTTP status).
 * Align with the RTTS API contract §2 acquire-error table.
 *
 * Retry is reserved for 5xx / network only — typed 4xx (including
 * vod_too_long and quota 429s) never show the generic Retry affordance.
 */

import type { RttsAcquireErrorCode, RttsAcquireFailure } from '../types';

export type MappedAcquireError = {
  message: string;
  /** True only for 5xx — UI shows Retry. Network failures are separate. */
  showRetry: boolean;
  /** Soft toast instead of red error (e.g. reel_already_exists). */
  quiet?: boolean;
};

const STATIC_COPY: Partial<Record<RttsAcquireErrorCode, string>> = {
  not_entitled: 'Scoutlytics Studio is not enabled for this league.',
  not_broadcaster: 'Only the broadcaster of this game can generate a reel.',
  feature_disabled: 'Scoutlytics Studio is temporarily unavailable.',
  reel_already_exists: 'A reel already exists for this game.',
  retry_limit_exceeded:
    'Highlight retry limit reached for this game. Contact support if you need another attempt.',
  too_many_in_flight:
    'Too many highlight jobs are already in progress for this league. Try again when one finishes.',
  rate_limited:
    'Highlight acquire rate limit reached for this league. Try again later.',
  daily_capacity_reached:
    'Daily highlight capacity has been reached. Try again tomorrow.',
};

export const NETWORK_ACQUIRE_ERROR_COPY =
  'Something went wrong. Check your connection and try again.';

export function isKnownAcquireErrorCode(
  value: string
): value is RttsAcquireErrorCode {
  return (
    value === 'not_entitled' ||
    value === 'not_broadcaster' ||
    value === 'feature_disabled' ||
    value === 'reel_already_exists' ||
    value === 'vod_too_long' ||
    value === 'retry_limit_exceeded' ||
    value === 'too_many_in_flight' ||
    value === 'rate_limited' ||
    value === 'daily_capacity_reached' ||
    value === 'unknown'
  );
}

function formatVodTooLong(failure: RttsAcquireFailure): string {
  const vod =
    typeof failure.vod_minutes === 'number' && Number.isFinite(failure.vod_minutes)
      ? Math.trunc(failure.vod_minutes)
      : null;
  const max =
    typeof failure.max_minutes === 'number' && Number.isFinite(failure.max_minutes)
      ? Math.trunc(failure.max_minutes)
      : null;
  if (vod != null && max != null) {
    return `This game is ${vod} min — reels are limited to ${max} min`;
  }
  if (max != null) {
    return `This game is too long — reels are limited to ${max} min`;
  }
  return 'This game is too long for a highlight reel.';
}

/**
 * Map a typed acquire failure from the API/client into UI copy.
 */
export function mapAcquireError(failure: RttsAcquireFailure): MappedAcquireError {
  const code = failure.error;
  const status = failure.status ?? 0;

  // 5xx — even if a body error string is present, offer Retry.
  if (status >= 500) {
    return {
      message: failure.message?.trim() || NETWORK_ACQUIRE_ERROR_COPY,
      showRetry: true,
    };
  }

  if (code === 'vod_too_long') {
    return { message: formatVodTooLong(failure), showRetry: false };
  }

  if (code === 'reel_already_exists') {
    return {
      message: STATIC_COPY.reel_already_exists!,
      showRetry: false,
      quiet: true,
    };
  }

  const staticCopy = STATIC_COPY[code];
  if (staticCopy) {
    return { message: staticCopy, showRetry: false };
  }

  // Unknown typed 4xx — prefer server message; never pretend it's a network blip.
  const fallback =
    failure.message?.trim() ||
    'Could not start this reel.';
  return { message: fallback, showRetry: false };
}
